/* KD9 Mobile — independent, touch-first login; shares the existing auth backend. */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Eye, EyeOff, LockKeyhole, Mail,
  UserRoundPlus, AlertCircle, Compass
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { MOBILE_MODE_KEY } from "./mobileNavigation";
import "./mobile-auth.css";

export default function MobileLogin() {
  const navigate = useNavigate();
  const { ready, user, blocked, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    try { window.sessionStorage.setItem(MOBILE_MODE_KEY, "1"); }
    catch (_) { /* The form remains usable even with restricted storage. */ }
  }, []);

  useEffect(() => {
    if (blocked) {
      navigate("/cuenta-bloqueada", { replace: true });
    } else if (ready && user) {
      // Do not assume mobile eligibility here: AppShellSwitch validates the
      // real /api/me/access-map before rendering any mobile navigation.
      navigate("/app/inicio", { replace: true });
    }
  }, [blocked, ready, user, navigate]);

  async function handleSubmit(event) {
    event.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);
    try {
      const result = await login(email.trim(), password);
      if (result.ok) {
        navigate("/app/inicio", { replace: true });
      } else if (result.verificationRequired) {
        // Existing WhatsApp activation stays unchanged, including backend OTP.
        const activation = {
          activationToken: result.activationToken,
          phoneMasked: result.phoneMasked,
          otpSent: false,
          destination: "/app/inicio",
        };
        try {
          window.sessionStorage.setItem("kd9-whatsapp-activation", JSON.stringify(activation));
        } catch (_) { /* The router state carries the activation on this navigation. */ }
        navigate("/verificar-whatsapp", { replace: true, state: activation });
      } else {
        setError(result.error || "No pudimos iniciar sesión. Verifica tus datos.");
      }
    } catch (_) {
      setError("No pudimos completar el acceso. Inténtalo nuevamente.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="kd9-mobile-auth" data-testid="kd9-mobile-login">
      <div className="kd9-mobile-auth-glow" aria-hidden="true" />
      <div className="kd9-mobile-auth-content">
        <header className="kd9-mobile-auth-heading">
          <img className="kd9-mobile-auth-logo" src="/logos/kd9-master.png" alt="KHAOS DELTA 9" width="104" height="104" draggable="false" />
          <span className="kd9-mobile-auth-eyebrow">KHAOS DELTA 9</span>
          <h1>Entra a tu universo</h1>
          <p>La energía que despierta lo ancestral.</p>
        </header>

        <section className="kd9-mobile-auth-panel" aria-label="Acceso a KD9 móvil">
          <div className="kd9-mobile-auth-panel-heading">
            <span><Compass size={19} aria-hidden="true" /> MI PORTAL KD9</span>
            <h2>Bienvenido de nuevo</h2>
            <p>Todo tu camino KD9, desde tu celular.</p>
          </div>
          {error && (
            <div className="kd9-mobile-auth-error" role="alert" data-testid="mobile-login-error">
              <AlertCircle size={18} aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}
          <form onSubmit={handleSubmit} data-testid="mobile-login-form">
            <label className="kd9-mobile-auth-label" htmlFor="kd9-mobile-email">Correo electrónico</label>
            <div className="kd9-mobile-auth-control">
              <Mail size={20} aria-hidden="true" />
              <input id="kd9-mobile-email" type="email" inputMode="email" autoComplete="username"
                placeholder="tucorreo@ejemplo.com" value={email}
                onChange={(e) => setEmail(e.target.value)} required
                disabled={loading} data-testid="mobile-login-email" />
            </div>
            <label className="kd9-mobile-auth-label" htmlFor="kd9-mobile-password">Contraseña</label>
            <div className="kd9-mobile-auth-control">
              <LockKeyhole size={20} aria-hidden="true" />
              <input id="kd9-mobile-password" type={visible ? "text" : "password"}
                autoComplete="current-password" placeholder="Ingresa tu contraseña"
                value={password} onChange={(e) => setPassword(e.target.value)}
                required disabled={loading} data-testid="mobile-login-password" />
              <button type="button" className="kd9-mobile-auth-eye"
                onClick={() => setVisible((current) => !current)}
                aria-label={visible ? "Ocultar contraseña" : "Mostrar contraseña"}
                aria-pressed={visible} disabled={loading} data-testid="mobile-login-password-toggle">
                {visible ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            <Link className="kd9-mobile-auth-help" to="/contacto">¿Necesitas ayuda para ingresar?</Link>
            <button type="submit" className="kd9-mobile-auth-submit" disabled={loading}
              data-testid="mobile-login-submit">
              {loading ? "Validando acceso…" : "Entrar a KD9"}
              {!loading && <ArrowRight size={20} aria-hidden="true" />}
            </button>
          </form>
        </section>

        <Link to="/register" className="kd9-mobile-auth-register">
          <UserRoundPlus size={19} aria-hidden="true" />
          <span>¿Aún no tienes cuenta? <strong>Crear mi cuenta</strong></span>
        </Link>

      </div>
    </main>
  );
}
